import React, { useState } from 'react';
import { Container, Typography, Button, Box, Grid, Card, CardContent, Paper, TextField, CircularProgress, Alert, Chip, Avatar, keyframes } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BoltIcon from '@mui/icons-material/Bolt';
import BalanceIcon from '@mui/icons-material/Balance';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ExampleLetterCarousel from '../components/ExampleLetterCarousel';
import ExampleLetterModal from '../components/ExampleLetterModal';

// Floating animation for background elements
const float = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px); }
  25% { transform: translateY(-20px) translateX(10px); }
  50% { transform: translateY(-10px) translateX(-10px); }
  75% { transform: translateY(-15px) translateX(5px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0,212,255,0.3), 0 0 40px rgba(0,212,255,0.1); }
  50% { box-shadow: 0 0 40px rgba(0,212,255,0.6), 0 0 80px rgba(0,212,255,0.3); }
`;

// Example letter for the modal
const exampleLetter = {
  company: 'Amazon',
  companyIcon: '📦',
  headline: 'Double-charged for Prime membership',
  result: 'Full refund of $139 received in 1 business day',
  laws: ['15 U.S.C. § 1666', 'FTC Act § 5', 'State Consumer Protection'],
  fullLetter: `[Your Name]
[Your Address]
[Date]

Amazon Customer Service
P.O. Box 81226
Seattle, WA 98108

Re: Unauthorized Duplicate Charge - Amazon Prime Membership

Dear Amazon Customer Service,

I am writing to formally dispute an unauthorized duplicate charge on my account for Amazon Prime membership in the amount of $139.00, posted on [date].

FACTUAL BACKGROUND

On [date], I was charged twice for my annual Amazon Prime membership renewal. My bank statement shows two identical charges of $139.00, reference numbers [XXX] and [YYY]. I have been a Prime member since [year] and have never experienced billing errors of this nature.

LEGAL BASIS FOR DISPUTE

1. Fair Credit Billing Act (15 U.S.C. § 1666)
   Under federal law, I have the right to dispute billing errors within 60 days. This unauthorized duplicate charge constitutes a clear billing error requiring immediate correction.

2. FTC Act Section 5 (15 U.S.C. § 45)
   The Federal Trade Commission has established that deceptive practices in commerce, including billing errors that are not promptly corrected, violate federal consumer protection standards.

3. Electronic Fund Transfer Act (15 U.S.C. § 1693)
   For electronic transactions, merchants must provide clear documentation and correct unauthorized charges within specific timeframes.

4. State Consumer Protection Laws
   [Your State] consumer protection statutes require merchants to honor refund requests for duplicate charges and provide remedies for billing errors.

REQUESTED REMEDY

I request immediate action within 14 business days:

1. Full refund of the duplicate $139.00 charge
2. Written confirmation that the error has been corrected
3. Assurance that my Prime membership will not be affected

CONSEQUENCES OF NON-COMPLIANCE

Failure to resolve this matter promptly may result in:
• Filing a complaint with the Federal Trade Commission
• Dispute with credit card issuer under Regulation Z
• Report to state Attorney General's Consumer Protection Division
• Better Business Bureau complaint
• Negative review on consumer protection websites

SUPPORTING DOCUMENTATION

Enclosed please find:
• Bank statement showing duplicate charges
• Screenshots of transaction history
• Account information

I have been a loyal Amazon customer for [X] years with an excellent payment history. I trust this matter will be resolved promptly and professionally.

Please respond in writing within 14 business days to confirm resolution.

Sincerely,

[Your Signature]
[Your Name]
[Account Number: XXX-XXXXX]
[Email: your.email@example.com]
[Phone: (XXX) XXX-XXXX]`
};

function Landing() {
  const navigate = useNavigate();
  const [demoIssue, setDemoIssue] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoLetter, setDemoLetter] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [exampleModalOpen, setExampleModalOpen] = useState(false);

  // Check for session expired message
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('message') === 'session_expired') {
      setSessionExpired(true);
      window.history.replaceState({}, '', '/');
      // Clear it after 5 seconds
      setTimeout(() => setSessionExpired(false), 5000);
    }
  }, []);

  const handleDemoGeneration = () => {
    if (!demoIssue.trim()) return;
    
    setDemoLoading(true);
    
    // Simulate AI generation with a realistic demo letter
    setTimeout(() => {
      const companyType = demoIssue.includes('Amazon') ? 'Amazon Customer Service' : 
        demoIssue.includes('Delta') || demoIssue.includes('United') || demoIssue.includes('airline') ? 'Customer Relations Department' :
        'Customer Service Department';
      
      setDemoLetter(`[Your Name]\n[Your Address]\n[Date]\n\n${companyType}\n\nDear Sir or Madam,\n\nI am writing to formally dispute ${demoIssue.toLowerCase().includes('charge') ? 'a billing error' : 'a service failure'} that occurred on my account. ${demoIssue}\n\nPursuant to the Fair Credit Billing Act (15 U.S.C. § 1666), I am entitled to dispute billing errors within 60 days. Additionally, your company's failure to provide the contracted service constitutes a breach under the Uniform Commercial Code (UCC § 2-601).\n\nUnder federal law [15 U.S.C. § 45], deceptive practices in commerce are prohibited. The Federal Trade Commission has established that companies must honor refund policies and correct billing errors promptly.\n\n[⚠️ PREVIEW ONLY - Sign up to see full letter with specific legal citations]\n\nI request immediate resolution within 14 business days. Failure to respond may result in:\n• Filing with the Better Business Bureau\n• Consumer complaint with the FTC\n• Credit card chargeback under Regulation Z\n• Report to state Attorney General\n\nI look forward to your prompt response.\n\nSincerely,\n[Your Name]`);
      setDemoLoading(false);
    }, 2000);
  };

  return (
    <Box>
      <Helmet>
        <title>ComplainAI - AI Dispute Letter Generator | Get Refunds from Amazon, Airlines & More</title>
        <meta name="description" content="Generate professional dispute letters with legal citations in seconds. Get refunds from Amazon, airlines, hotels, gyms, and subscriptions. Free AI-powered consumer rights tool." />
        <meta name="keywords" content="dispute letter generator, refund letter, chargeback letter, consumer rights, AI letter writer, Amazon refund, airline compensation, credit card dispute" />
        <meta name="google-site-verification" content="vMPtPCam6vXzrozy350XhalZXuUBhRhQeRlmiIZ8c2c" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ComplainAI - Get Your Money Back with AI Letters" />
        <meta property="og:description" content="AI-powered dispute letters that cite actual consumer protection laws. Get refunds, lost items returned, and more." />
        <meta property="og:image" content="/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ComplainAI - Fight Back Against Unfair Treatment" />
        <meta name="twitter:description" content="Generate professional dispute letters in seconds. 1 free letter to get started." />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ComplainAI",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "127"
            },
            "description": "AI-powered dispute letter generator for consumer issues"
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: '#0a0e17',
          color: 'white',
          py: { xs: 8, md: 12 },
          px: 2,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            animation: `${float} 20s ease-in-out infinite`,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(0, 255, 136, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            animation: `${float} 25s ease-in-out infinite reverse`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Session Expired Alert */}
          {sessionExpired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your session expired. Please log in again to continue.
            </Alert>
          )}
          
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: { xs: 3, md: 4 },
              animation: `${fadeInUp} 0.8s ease-out`,
            }}
          >
            <Box
              component="img"
              src="/logo.png" 
              alt="ComplainAI" 
              sx={{ 
                height: { xs: '80px', sm: '100px', md: '120px' },
                width: 'auto',
                filter: 'drop-shadow(0 12px 40px rgba(0, 212, 255, 0.35))',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }} 
            />
          </Box>
          <Typography 
            variant="h2" 
            className="hero-title" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 800, 
              fontSize: { xs: '2.5rem', md: '3.5rem' }, 
              animation: `${fadeInUp} 0.8s ease-out 0.1s backwards`,
              color: '#00d4ff',
              letterSpacing: '-0.02em',
            }}
          >
            Get What You Deserve
          </Typography>
          <Typography 
            variant="h5" 
            className="hero-subtitle" 
            align="center" 
            paragraph 
            sx={{ 
              fontSize: { xs: '1.25rem', md: '1.5rem' }, 
              lineHeight: 1.6, 
              animation: `${fadeInUp} 0.8s ease-out 0.2s backwards`,
              fontWeight: 500,
              opacity: 0.95,
            }}
          >
            Refunds. Apologies. Lost items returned. Problems fixed.
          </Typography>
          <Typography 
            variant="body1" 
            className="hero-subtitle" 
            align="center" 
            paragraph 
            sx={{ 
              opacity: 0.85, 
              fontSize: '1.1rem', 
              maxWidth: '700px', 
              mx: 'auto',
              lineHeight: 1.8,
              animation: `${fadeInUp} 0.8s ease-out 0.3s backwards`,
            }}
          >
            AI writes professional letters that companies actually respond to — with real laws cited.
          </Typography>
          <Box 
            sx={{ 
              mt: 5, 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexWrap: 'wrap',
              animation: `${fadeInUp} 0.8s ease-out 0.4s backwards`,
            }}
          >
            <Button 
              variant="contained" 
              size="large" 
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#00d4ff', 
                color: '#0a0e17', 
                fontWeight: 700,
                px: { xs: 4, md: 5 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: '1rem', md: '1.2rem' },
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 212, 255, 0.4)',
                width: { xs: '100%', sm: 'auto' },
                position: 'relative',
                overflow: 'hidden',
                animation: `${glow} 2s ease-in-out infinite`,
                '&:hover': { 
                  bgcolor: '#00b8e6',
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 16px 48px rgba(0, 212, 255, 0.5)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  transition: 'left 0.5s',
                },
                '&:hover::before': {
                  left: '100%',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get My Free Letter
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.3)', 
                borderWidth: 2,
                color: 'white',
                fontWeight: 600,
                px: { xs: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                borderRadius: '12px',
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { 
                  borderWidth: 2,
                  borderColor: 'rgba(255, 255, 255, 0.5)', 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  transform: 'translateY(-4px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              How It Works
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={() => setExampleModalOpen(true)}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.3)', 
                borderWidth: 2,
                color: 'white',
                fontWeight: 600,
                px: { xs: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                borderRadius: '12px',
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { 
                  borderWidth: 2,
                  borderColor: 'rgba(255, 255, 255, 0.5)', 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  transform: 'translateY(-4px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              See Example
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Instant Demo - No Sign-Up Required */}
      <Box sx={{ bgcolor: '#0a0e17', py: { xs: 8, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Chip 
              icon={<AutoAwesomeIcon />} 
              label="Try It Now - No Sign-Up" 
              sx={{ 
                mb: 3, 
                fontWeight: 700, 
                fontSize: '1rem', 
                py: 3, 
                px: 2,
                bgcolor: 'rgba(0, 212, 255, 0.15)', 
                color: '#00d4ff',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                backdropFilter: 'blur(10px)',
              }} 
            />
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' }, 
                fontWeight: 700, 
                color: 'white',
                mb: 2,
              }}
            >
              See the Magic in Action
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '600px', 
                mx: 'auto', 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontWeight: 400, 
                fontSize: '1.15rem',
                lineHeight: 1.7,
              }}
            >
              Describe any issue - we'll generate a professional letter instantly
            </Typography>
          </Box>

          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 3,
              bgcolor: 'rgba(26, 35, 50, 0.6)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 48px rgba(0, 212, 255, 0.2)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
              },
            }}
          >
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Try: 'Delta lost my luggage and won't return it' or 'Amazon charged me twice for Prime'"
              value={demoIssue}
              onChange={(e) => setDemoIssue(e.target.value)}
              disabled={demoLoading}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(10, 14, 23, 0.4)',
                  color: 'white',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 212, 255, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00d4ff',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleDemoGeneration}
              disabled={!demoIssue.trim() || demoLoading}
              startIcon={demoLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              sx={{ 
                py: 2, 
                fontSize: '1.1rem', 
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: '#00d4ff',
                color: '#0a0e17',
                boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                '&:hover': {
                  bgcolor: '#00b8e6',
                  boxShadow: '0 6px 24px rgba(0, 212, 255, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(0, 212, 255, 0.3)',
                  color: 'rgba(10, 14, 23, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {demoLoading ? 'Generating Letter...' : 'Generate Sample Letter (Free)'}
            </Button>

            {demoLetter && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ✨ Preview Generated! Sign up free to get the full letter with all legal citations.
                  </Typography>
                </Alert>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'background.default', 
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    position: 'relative',
                    maxHeight: '400px',
                    overflow: 'hidden'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'monospace',
                      lineHeight: 1.8,
                      fontSize: '0.9rem'
                    }}
                  >
                    {demoLetter}
                  </Typography>
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0, 
                      height: '150px',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,1) 100%)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      pb: 2
                    }}
                  >
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/register')}
                      sx={{ fontWeight: 600 }}
                    >
                      Sign Up Free to Get Full Letter
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Real Letters That Got Results */}
      <ExampleLetterCarousel />

      {/* How It Works */}
      <Box sx={{ bgcolor: '#0a0e17', py: { xs: 8, md: 10 } }} id="how-it-works">
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              mb: 6, 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.75rem' },
              color: 'white',
            }}
          >
            How It Works
          </Typography>
          
          {/* Step Cards - Visual & Scannable */}
          <Grid container spacing={3} alignItems="stretch">
            {/* Step 1 */}
            <Grid item xs={12} md={4}>
              <Box 
                sx={{
                  bgcolor: 'rgba(26, 35, 50, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    border: '1px solid rgba(0, 212, 255, 0.4)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                {/* Big Step Number */}
                <Box 
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    borderRadius: '50%',
                    bgcolor: '#00d4ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0, 212, 255, 0.4)',
                  }}
                >
                  <Typography sx={{ color: '#0a0e17', fontWeight: 800, fontSize: '1.75rem' }}>1</Typography>
                </Box>
                
                {/* Icon */}
                <Typography sx={{ fontSize: '2.5rem', mb: 2 }}>✍️</Typography>
                
                {/* Title */}
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  Tell Us What Happened
                </Typography>
                
                {/* One-liner */}
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>
                  "Amazon charged me twice" or "Delta lost my bag"
                </Typography>
              </Box>
            </Grid>

            {/* Arrow connector (desktop only) */}
            <Box sx={{ display: { xs: 'none', md: 'none' } }} />

            {/* Step 2 */}
            <Grid item xs={12} md={4}>
              <Box 
                sx={{
                  bgcolor: 'rgba(26, 35, 50, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    border: '1px solid rgba(0, 212, 255, 0.4)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                <Box 
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    borderRadius: '50%',
                    bgcolor: '#00d4ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0, 212, 255, 0.4)',
                  }}
                >
                  <Typography sx={{ color: '#0a0e17', fontWeight: 800, fontSize: '1.75rem' }}>2</Typography>
                </Box>
                
                <Typography sx={{ fontSize: '2.5rem', mb: 2 }}>⚡</Typography>
                
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  AI Writes Your Letter
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>
                  With real laws cited (FTC, UCC, state regs)
                </Typography>
              </Box>
            </Grid>

            {/* Step 3 */}
            <Grid item xs={12} md={4}>
              <Box 
                sx={{
                  bgcolor: 'rgba(26, 35, 50, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    border: '1px solid rgba(0, 212, 255, 0.4)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                <Box 
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    borderRadius: '50%',
                    bgcolor: '#00d4ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0, 212, 255, 0.4)',
                  }}
                >
                  <Typography sx={{ color: '#0a0e17', fontWeight: 800, fontSize: '1.75rem' }}>3</Typography>
                </Box>
                
                <Typography sx={{ fontSize: '2.5rem', mb: 2 }}>💰</Typography>
                
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  Send & Get Results
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>
                  Most companies respond within 7-14 days
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* CTA under steps */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#00d4ff', 
                color: '#0a0e17',
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
                '&:hover': {
                  bgcolor: '#00b8e6',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 24px rgba(0, 212, 255, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Try It Free →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ bgcolor: '#0a0e17', py: { xs: 8, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(0, 255, 136, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.75rem' },
              mb: 7,
            }}
          >
            Why It Works
          </Typography>
          <Grid container spacing={5} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'rgba(26, 35, 50, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    bgcolor: 'rgba(26, 35, 50, 0.6)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    bgcolor: 'rgba(0, 212, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <BalanceIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 2 }}>Real Legal Citations</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
                  Every letter cites relevant consumer protection laws (15 U.S.C. § 45, state regs, UCC)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'rgba(26, 35, 50, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    bgcolor: 'rgba(26, 35, 50, 0.6)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    bgcolor: 'rgba(0, 212, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <BoltIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 2 }}>10x Faster Than ChatGPT</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
                  Grok's 2M context window means smarter, more precise legal reasoning
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'rgba(26, 35, 50, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    bgcolor: 'rgba(26, 35, 50, 0.6)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    bgcolor: 'rgba(0, 212, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <AttachMoneyIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                  Average Recovery: $237
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
                  Users recover an average of $237 per successful letter
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Social Proof - Success Stories */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#0a0e17', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute',
          top: '30%',
          right: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, color: 'white' }}>
              Real People, Real Results
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: '600px', mx: 'auto', color: 'rgba(255, 255, 255, 0.7)' }}>
              Join 1,200+ people who've gotten results with ComplainAI
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ 
                height: '100%', 
                bgcolor: 'rgba(26, 35, 50, 0.5)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#00d4ff', mr: 2, fontWeight: 700 }}>JM</Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>Jessica M.</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[1,2,3,4,5].map(i => <StarIcon key={i} sx={{ fontSize: 14, color: '#ffc107' }} />)}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, lineHeight: 1.7 }}>
                    "United lost my checked bag with $800 of wedding clothes. Sent the ComplainAI letter, got full reimbursement in 6 days!"
                  </Typography>
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="Got $800 back" 
                    size="small" 
                    sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50', border: '1px solid rgba(76, 175, 80, 0.3)' }} 
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ 
                height: '100%', 
                bgcolor: 'rgba(26, 35, 50, 0.5)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#00d4ff', mr: 2, fontWeight: 700 }}>RC</Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>Robert C.</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[1,2,3,4,5].map(i => <StarIcon key={i} sx={{ fontSize: 14, color: '#ffc107' }} />)}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, lineHeight: 1.7 }}>
                    "Amazon charged me twice for Prime. Their support gave me runaround. Used ComplainAI - refund hit my account next day!"
                  </Typography>
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="Got $139 back" 
                    size="small" 
                    sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50', border: '1px solid rgba(76, 175, 80, 0.3)' }} 
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ 
                height: '100%', 
                bgcolor: 'rgba(26, 35, 50, 0.5)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#00d4ff', mr: 2, fontWeight: 700 }}>SK</Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>Sarah K.</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[1,2,3,4,5].map(i => <StarIcon key={i} sx={{ fontSize: 14, color: '#ffc107' }} />)}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, lineHeight: 1.7 }}>
                    "My gym kept charging after I cancelled. The legal citations made them refund 3 months immediately. Best $10 I've spent!"
                  </Typography>
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="Got $147 back" 
                    size="small" 
                    sx={{ fontWeight: 600, bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50', border: '1px solid rgba(76, 175, 80, 0.3)' }} 
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#00d4ff' }}>
              $287,000+ Recovered
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Average result: $237 per successful letter
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 } }} id="pricing">
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom 
          sx={{ 
            mb: 2,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' },
          }}
        >
          Simple Pricing
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          color="text.secondary" 
          sx={{ 
            mb: 6, 
            maxWidth: '500px', 
            mx: 'auto',
            fontSize: '1.1rem',
          }}
        >
          Try free. Upgrade when you're ready.
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }} justifyContent="center">
          {/* Free Plan */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                bgcolor: 'rgba(26, 35, 50, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease',
                height: '100%',
                '&:hover': {
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                },
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>Free</Typography>
              <Typography variant="h3" color="primary" sx={{ fontSize: '3rem', fontWeight: 700 }}>$0</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>forever</Typography>
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">3 dispute letters</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">AI-generated with legal citations</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">Email support</Typography>
                </Box>
              </Box>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={() => navigate('/register')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: 'rgba(0, 212, 255, 0.3)',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(0, 212, 255, 0.05)',
                  },
                }}
              >
                Get Free Letter
              </Button>
            </Paper>
          </Grid>
          
          {/* Pro Plan */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                bgcolor: 'rgba(0, 212, 255, 0.1)',
                border: '2px solid #00d4ff',
                position: 'relative',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 16px 48px rgba(0, 212, 255, 0.3)',
                },
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#00d4ff',
                  color: '#0a0e17',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              >
                ⚡ BEST VALUE
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 1 }}>Pro</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                <Typography variant="h3" color="primary" sx={{ fontSize: '3rem', fontWeight: 700 }}>$9.99</Typography>
                <Typography variant="body2" color="text.secondary">/mo</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(0, 212, 255, 0.8)', display: 'block', mb: 3 }}>
                Less than 1 coffee ☕
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#00d4ff', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>10 letters/month</strong> (most need 3-5)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#00d4ff', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Success prediction</strong> — know your odds</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#00d4ff', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Auto-escalation</strong> to BBB & FTC</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#00d4ff', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Chargeback letters</strong> for banks</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: '#00d4ff', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}>Priority support</Typography>
                </Box>
              </Box>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => navigate('/register')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  bgcolor: '#00d4ff',
                  color: '#0a0e17',
                  boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                  '&:hover': {
                    bgcolor: '#00b8e6',
                    boxShadow: '0 6px 24px rgba(0, 212, 255, 0.4)',
                  },
                }}
              >
                Start Pro — Cancel Anytime
              </Button>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mt: 2 }}>
                Average user recovers $237 → 23x ROI
              </Typography>
            </Paper>
          </Grid>

          {/* Enterprise Plan */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                bgcolor: 'rgba(26, 35, 50, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: '1px solid rgba(138, 43, 226, 0.5)',
                  boxShadow: '0 16px 48px rgba(138, 43, 226, 0.2)',
                },
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#8a2be2',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              >
                🏢 TEAMS & BUSINESS
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 1 }}>Enterprise</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                <Typography variant="h3" sx={{ fontSize: '3rem', fontWeight: 700, color: '#8a2be2' }}>$29.99</Typography>
                <Typography variant="body2" color="text.secondary">/mo</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(138, 43, 226, 0.8)', display: 'block', mb: 3 }}>
                Best value for high-volume users
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#8a2be2', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Unlimited letters</strong></Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#8a2be2', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>API access</strong> for your systems</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#8a2be2', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Multi-user accounts</strong></Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#8a2be2', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}><strong>Custom templates</strong> for your industry</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: '#8a2be2', fontSize: 20, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white' }}>Dedicated account manager</Typography>
                </Box>
              </Box>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => navigate('/register')}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  bgcolor: '#8a2be2',
                  color: 'white',
                  boxShadow: '0 4px 16px rgba(138, 43, 226, 0.3)',
                  '&:hover': {
                    bgcolor: '#9d4edd',
                    boxShadow: '0 6px 24px rgba(138, 43, 226, 0.4)',
                  },
                }}
              >
                Start Enterprise — Cancel Anytime
              </Button>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mt: 2 }}>
                Perfect for high-volume users & businesses
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA */}
      <Box sx={{ bgcolor: '#0a0e17', color: 'white', py: { xs: 8, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.75rem', md: '2.5rem' }, 
              fontWeight: 700,
              mb: 3,
            }}
          >
            Stop letting companies get away with bad service
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            paragraph 
            sx={{ 
              fontSize: '1.25rem', 
              maxWidth: '600px', 
              mx: 'auto',
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 5,
            }}
          >
            Join thousands getting what they're owed
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#00d4ff', 
                color: '#0a0e17',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 212, 255, 0.4)',
                animation: `${glow} 2s ease-in-out infinite`,
                '&:hover': {
                  bgcolor: '#00b8e6',
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 48px rgba(0, 212, 255, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Create Your First Letter — Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Example Letter Modal */}
      <ExampleLetterModal 
        letter={exampleLetter}
        open={exampleModalOpen}
        onClose={() => setExampleModalOpen(false)}
      />
    </Box>
  );
}

export default Landing;
